/**
 * The shapes the import-review tagging loop shares between the API, the app
 * and the eval: what the model suggested, what the reviewer decided, and the
 * label that decision leaves behind.
 */
import type { CuisineChoice } from '../utils/cuisineVocabulary';
import type { CanonicalDishType } from '../utils/dishTypeVocabulary';

export type MetadataField = 'cuisine' | 'dishType' | 'diet';
export const METADATA_FIELDS: readonly MetadataField[] = ['cuisine', 'dishType', 'diet'];

/**
 * A recipe's time, in minutes. Reviewed like a tag, but a number, not a
 * choice from a list, so it lives beside the three tag fields rather than in
 * METADATA_FIELDS (which every list-of-values helper iterates).
 */
export interface TimeValue {
  prep: number;
  cook: number;
  total: number;
}

/** Everything the Review screen settles: the three tags and the time. */
export type ReviewField = MetadataField | 'time';
export const REVIEW_FIELDS: readonly ReviewField[] = [...METADATA_FIELDS, 'time'];

/** `meat` is never stored in `diets`; it is only ever known from a review. */
export type DietChoice = 'meat' | 'fish' | 'veg';
export const DIET_CHOICES: readonly DietChoice[] = ['meat', 'fish', 'veg'];

export interface FieldSuggestion<T> {
  /** null = the classifier declined. Never a guess dressed as an answer. */
  value: T | null;
  /** 0-100. */
  confidence: number;
  /** `publisher` = the page's own tag, `rules` = our heuristics, `model` = Gemini. */
  source: 'publisher' | 'rules' | 'model';
}

/**
 * Something wrong with the six inputs themselves. Warnings, never blocking:
 * the reviewer decides whether to edit, re-enrich or reject.
 */
export type MetadataCheckCode =
  | 'image_mismatch'
  | 'image_missing'
  | 'unused_ingredients'
  | 'missing_ingredients'
  | 'prep_exceeds_total'
  | 'parts_exceed_total'
  | 'total_default'
  | 'total_missing'
  /** A model-written timeline the checks refused (#850): its numbers wait for a person. */
  | 'timeline_disputed'
  /** The same, on a time a person set before #850: the two numbers wait side by side. */
  | 'timeline_person';

export interface MetadataCheck {
  code: MetadataCheckCode;
  /** Short free text from the model ("shows a salad, not a stew"). */
  detail?: string;
  /** Ingredient names, for the two ingredient checks. */
  items?: string[];
}

export interface MetadataSuggestion {
  cuisine: FieldSuggestion<CuisineChoice>;
  dishType: FieldSuggestion<CanonicalDishType>;
  diet: FieldSuggestion<DietChoice>;
  /**
   * The publisher's time when the recipe has one (the model only cross-checks
   * it), otherwise the model's estimate. Absent on suggestions made before
   * times were reviewed, which is how the screen knows to ask again.
   */
  time?: FieldSuggestion<TimeValue>;
  checks: MetadataCheck[];
  model: string;
  /** Bumped whenever the prompt changes, so the eval can compare versions. */
  promptVersion: string;
  /** False when the photo could not be fetched; the model judged text only. */
  imageSeen: boolean;
  fewShotCount: number;
  suggestedAt: string;
}

export interface MetadataDecisions {
  cuisine?: CuisineChoice;
  dishType?: CanonicalDishType;
  diet?: DietChoice;
  time?: TimeValue;
}

/**
 * `single` = the reviewer looked at this row and answered it.
 * `glance` = "These N look right": every field was sure, the rows were shown
 * together and confirmed in one tap. Ground truth, scored apart from `single`.
 * `bulk` = the retired "Accept N confident", which nobody looked at. Still
 * read on old documents (drawn as "auto"), never written.
 */
export type ReviewMode = 'single' | 'glance' | 'bulk';

export interface MetadataReview extends MetadataDecisions {
  reviewedAt: string;
  /** The latest write's mode. Kept for older readers; `fieldModes` is per field. */
  reviewMode: ReviewMode;
  /**
   * How each field was settled. One recipe-level mode used to cover every
   * field, so a single answer after a bulk accept relabelled the bulk fields
   * as human. A field with no entry inherits `reviewMode`.
   */
  fieldModes?: Partial<Record<ReviewField, ReviewMode>>;
}

/** Where a review row lives, resolved on the server from the uid — never a path. */
export type ReviewScope = 'household' | 'personal' | 'catalogue';

/**
 * What a row is: a collection import still waiting (`import`), a recipe in
 * the cook's library (`library`) or a shared catalogue recipe (`catalogue`).
 */
export type ReviewSource = 'import' | 'library' | 'catalogue';

/**
 * How a field reads on the review screen.
 *   confirmed — a person settled it (`single` or `glance`)
 *   auto      — settled by the retired bulk accept, nobody looked
 *   sure      — not settled; a suggestion at or above the bulk threshold
 *   unsure    — not settled; a suggestion below it, or a value nobody checked
 *   missing   — not settled; nothing to show
 */
export type FieldState = 'confirmed' | 'auto' | 'sure' | 'unsure' | 'missing';

/** Content a reviewer can see is missing. `ingredients` and `steps` block Accept. */
export type ContentGap = 'ingredients' | 'steps' | 'image';

export type RowState =
  | 'ready-to-publish'
  | 'blocked'
  | 'to-check'
  | 'done'
  | 'looks-right'
  | 'ready';

/** How a reviewed field relates to what was suggested. */
export type ReviewOutcome = 'accepted' | 'corrected' | 'unsuggested';

/** One field of a `metadataLabels/{recipeId}` row. */
export interface FieldLabel {
  /** For `time`, the total in minutes as text; the parts are in humanTime. */
  human: string;
  suggested: string | null;
  confidence: number | null;
  source: FieldSuggestion<unknown>['source'] | null;
  outcome: ReviewOutcome;
  reviewMode: ReviewMode;
  promptVersion: string | null;
  labelledAt: string;
  /** `time` only. */
  humanTime?: TimeValue;
  suggestedTime?: TimeValue | null;
}

/**
 * `metadataLabels/{recipeId}` — ground truth for `npm run eval:recipe-metadata`
 * and the few-shot pool. Admin SDK only (no client rule).
 */
export interface MetadataLabel {
  recipeId: string;
  title: string;
  imageUrl?: string;
  preparationMinutes?: number;
  cookingMinutes?: number;
  readyInMinutes?: number;
  ingredients: string[];
  steps: string[];
  sourceUrl?: string;
  publisherCuisine?: unknown;
  fields: Partial<Record<ReviewField, FieldLabel>>;
  provenance: 'human';
  labelledBy: string;
  updatedAt: string;
}
